import { Component } from 'react';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { trackFollowButtonClick } from '../../utils/tracking/searchBrowse/trackFollowSearch';
import { locations } from '../../utils/tracking/shared/followSearchTracking';
import { getQueryParam } from '../../utils/uriUtils';
import { pageTypeConstants as PAGE_TYPES } from '../../constants/pageTypeConstants';
import {
    checkRewardedSeller,
    checkTradeUserUnderTopLevelLoyaltyTiers,
} from 'dibs-buyer-layout/exports/sellerBrandingHelpers';
import { isSellerBrandingRemovalTestVariant } from 'dibs-buyer-layout/exports/sellerBrandingRemovalAbTestHelpers';

// Components
import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton';
import { TooltipLoader } from '../../global/TooltipLoader';
import { FormattedMessage } from 'dibs-react-intl';

class SharedFollowTopButtonComponent extends Component {
    constructor() {
        super();

        this.handleFollowSearchCompleted = this.handleFollowSearchCompleted.bind(this);
        this.shouldAutoFollow = this.shouldAutoFollow.bind(this);

        this.state = {
            showSuccessTooltip: false,
            loadSuccessTooltip: false,
            showHoverTooltip: false,
            loadHoverTooltip: false,
            // Putting this in componentDidMount caused the FollowButton to update before we had user information.
            // We need user information when FollowButton updates to determine appropriate action
            shouldAutoFollow: this.shouldAutoFollow(),
        };
    }

    handleFollowSearchCompleted(isFollowing) {
        const { refetchFollow, itemSearch } = this.props;
        refetchFollow(() => {
            if (isFollowing) {
                // show success tooltip is user follows a search (but not if they unfollow)
                this.setState(
                    {
                        loadSuccessTooltip: true,
                        showSuccessTooltip: true,
                        showHoverTooltip: false,
                    },
                    () => {
                        // close success tooltip after 3 seconds
                        const timer = setTimeout(() => {
                            this.setState({ showSuccessTooltip: false });
                            clearTimeout(timer);
                        }, 3000);
                    }
                );
            }
        });

        trackFollowButtonClick({
            isFollowing,
            label: itemSearch.pageType === PAGE_TYPES.BUY ? 'top of buy page' : 'top of results',
        });
    }

    shouldAutoFollow() {
        if (typeof document !== 'undefined') {
            return getQueryParam(document.location.search, 'follow') === 'search';
        }
        return false;
    }

    render() {
        const {
            user,
            itemSearch,
            showFollowButton,
            followSearchValue,
            isFollowing,
            isCategorySearch,
            isClient,
            fullWidth,
            className: customClassName,
            serviceId,
        } = this.props;
        const {
            showHoverTooltip,
            loadHoverTooltip,
            loadSuccessTooltip,
            showSuccessTooltip,
            shouldAutoFollow,
        } = this.state;

        if (!showFollowButton) {
            return null;
        }

        // only show hover tooltip if user is not following search
        const mouseProps = {
            onMouseEnter: () =>
                this.setState({ showHoverTooltip: !isFollowing, loadHoverTooltip: !isFollowing }),
            onMouseLeave: () => this.setState({ showHoverTooltip: false }),
        };

        const getSuccessToolTipContent = () => {
            const pageType = itemSearch?.pageType;
            if (
                isClient &&
                (pageType === PAGE_TYPES.MORE_FROM_SELLER || pageType === PAGE_TYPES.DEALER)
            ) {
                const shouldShowSellerName =
                    !isSellerBrandingRemovalTestVariant() ||
                    checkRewardedSeller(itemSearch?.seller || null) ||
                    checkTradeUserUnderTopLevelLoyaltyTiers(user || null);
                if (!shouldShowSellerName) {
                    return (
                        <FormattedMessage
                            id="sb.SharedFollowTopButton.successTextHiddenSeller"
                            defaultMessage="Check your email for weekly updates."
                        />
                    );
                }
            }

            return (
                <FormattedMessage
                    id="sb.SharedFollowTopButton.successText"
                    defaultMessage="Check your email for weekly updates in {name}."
                    values={{
                        name: (
                            <span
                                data-tn="follow-search-value"
                                className={dibsCss.sassyFontBodyTypeHeavySizeNone}
                            >
                                {isCategorySearch ? followSearchValue : `"${followSearchValue}"`}
                            </span>
                        ),
                    }}
                />
            );
        };

        return (
            <div
                {...mouseProps}
                className={classnames(dibsCss.flexShrink0, dibsCss.relative, customClassName)}
            >
                <SaveSearchButton
                    fullWidth={fullWidth}
                    size="medium"
                    userId={user?.serviceId || null}
                    user={user}
                    page={itemSearch.displayUriRef}
                    name={followSearchValue}
                    autoFollow={shouldAutoFollow}
                    favoritesConnection={user?.favorites || null}
                    location={locations.SEARCH_BROWSE}
                    onFollowSearchCompleted={this.handleFollowSearchCompleted}
                    serviceId={serviceId}
                    itemSearch={itemSearch}
                />
                <TooltipLoader
                    load={loadHoverTooltip}
                    isVisible={showHoverTooltip}
                    closeOnOutsideClick
                    direction="right"
                >
                    <FormattedMessage
                        id="sb.SharedFollowTopButton.hoverText"
                        defaultMessage="Get weekly updates with new arrivals for this category."
                    />
                </TooltipLoader>
                <TooltipLoader
                    load={loadSuccessTooltip}
                    isVisible={showSuccessTooltip}
                    closeOnOutsideClick
                    direction="right"
                >
                    {getSuccessToolTipContent()}
                </TooltipLoader>
            </div>
        );
    }
}

SharedFollowTopButtonComponent.defaultProps = {
    onFollowSearchSuccess: () => {},
};

SharedFollowTopButtonComponent.propTypes = {
    user: PropTypes.object,
    itemSearch: PropTypes.object,
    showFollowButton: PropTypes.bool.isRequired,
    followSearchValue: PropTypes.string.isRequired,
    isFollowing: PropTypes.bool.isRequired,
    onFollowSearchSuccess: PropTypes.func,
    refetchFollow: PropTypes.func,
    isCategorySearch: PropTypes.bool.isRequired,
    fullWidth: PropTypes.bool,
    className: PropTypes.string,
    serviceId: PropTypes.string,
    isClient: PropTypes.bool,
};

const mapStateToProps = state => ({ isClient: state.relayVariables.variables.isClient });

export const SharedFollowTopButton = createFragmentContainer(
    connect(mapStateToProps)(SharedFollowTopButtonComponent),
    {
        itemSearch: graphql`
            fragment SharedFollowTopButton_itemSearch on ItemSearchQueryConnection {
                ...SaveSearchButton_itemSearch
                displayUriRef
                pageType
                seller {
                    ...sellerBrandingHelpers_seller
                }
            }
        `,
        user: graphql`
            fragment SharedFollowTopButton_user on User {
                serviceId
                ...sellerBrandingHelpers_user
                favorites(first: 1, types: $followSearchTypes, pages: $followSearchPages) {
                    ...SaveSearchButton_favoritesConnection
                }
            }
        `,
    }
);
